import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Layers, ChevronRight } from 'lucide-react'
import { supabase, isDemoMode } from '../../lib/supabase'
import { DemoNotice } from '../../lib/demo'

export default function ClientGroups() {
  const [groups, setGroups] = useState([])
  const [counts, setCounts] = useState({})
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')

  const load = async () => {
    if (isDemoMode) return
    setLoading(true)
    const [gRes, cRes] = await Promise.all([
      supabase.from('client_groups').select('*').is('archived_at', null).order('name'),
      supabase.from('companies').select('client_group_id').is('archived_at', null),
    ])
    setGroups(gRes.data || [])
    const counts = {}
    for (const row of cRes.data || []) {
      if (row.client_group_id) counts[row.client_group_id] = (counts[row.client_group_id] || 0) + 1
    }
    setCounts(counts)
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  const create = async (e) => {
    e.preventDefault()
    if (!newName.trim() || isDemoMode) return
    const { error } = await supabase.from('client_groups').insert({ name: newName.trim() })
    if (error) return alert(error.message)
    setNewName('')
    setCreating(false)
    load()
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <DemoNotice />

      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-800">Client Groups</h3>
            <p className="text-sm text-gray-500 mt-1">
              Parent organisations like Away Resorts. Each group has many sites (companies). Brand, contact and reporting roll up to the group.
            </p>
          </div>
          <button onClick={() => setCreating(c => !c)} className="btn-primary text-sm">
            <Plus className="w-4 h-4" /> New Group
          </button>
        </div>

        {creating && (
          <form onSubmit={create} className="flex gap-2 mb-3 p-3 bg-gray-50 rounded-lg">
            <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                   placeholder="Group name (e.g. Away Resorts)" className="input-field" autoFocus required />
            <button type="submit" className="btn-primary">Create</button>
            <button type="button" onClick={() => { setCreating(false); setNewName('') }} className="btn-secondary">Cancel</button>
          </form>
        )}

        {loading && <p className="text-sm text-gray-500">Loading…</p>}

        {!loading && groups.length === 0 && (
          <p className="text-sm text-gray-500 py-6 text-center">No client groups yet.</p>
        )}

        {groups.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {groups.map(g => (
              <li key={g.id}>
                <Link to={`/admin/client-groups/${g.id}`} className="flex items-center gap-3 py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors">
                  <Layers className="w-5 h-5 text-brand" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 truncate">{g.name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {counts[g.id] || 0} site{counts[g.id] === 1 ? '' : 's'}
                      {g.contact_email ? ` · ${g.contact_email}` : ''}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
